// ✅ NewTenantReceipt.jsx (component)
import React, { useEffect, useState } from "react";
import { Select, Spin,Radio  } from "antd";
import moment from "moment";
import CustomModal from "../../input-components/Modal";
import ShowValue from "../../input-components/ShowValue";
import TextBox from "../../input-components/textbox";

const NewTenantReceipt = ({
  visible,
  onClose,
  receiptDetails,
  supplierValue,
  tenantValue,
  ePaymentModes,
  selectedPaymentMode,
  setSelectedPaymentMode,
  optionSupplier,
  fetchingSupplier,
  debounceFetcherSupplier,
  handleSearchSupplier,
  handleClearSupplier,
  onSubmit,
  ToastRef,
  Tenant
}) => {
  const [amount, setAmount] = useState(0);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [rentAmount, setRentAmount] = useState(0);
  const [invoiceAmounts, setInvoiceAmounts] = useState([]);
  const [depositData, setDepositData] = useState([
    {
      description: `Deposit for ${receiptDetails?.propertyAddress || ""}`,
      amount: "",
    },
  ]);

  useEffect(() => {
    if (visible) {
      setAmount(0);
      setDate(moment().format("YYYY-MM-DD"));
      setRentAmount(0);
      setInvoiceAmounts([]);
      setDepositData([
        {
          description: `Deposit for ${receiptDetails?.propertyAddress || ""}`,
          amount: "",
        },
      ]);
    }
  }, [visible, receiptDetails]);

  const handleAmt = (e) => {
    const newAmt = parseFloat(e.target.value) || 0;
    setAmount(newAmt);
  
    const rent = parseFloat(receiptDetails.rent || 0);
    const invoices = receiptDetails.invoiceDueList || [];
  
    // Rent-first allocation
    let rentAllocated = 0;
    let invoiceAllocated = [];
    let depositAllocated = [
      {
        description: `Deposit for ${receiptDetails.propertyAddress}`,
        amount: "",
      },
    ];
    let remaining = newAmt;
  
    if (newAmt <= rent) {
      rentAllocated = newAmt;
      remaining = 0;
    } else {
      const numberOfPeriods = Math.floor(newAmt / rent);
      const calculatedRent = rent * numberOfPeriods;
      rentAllocated = calculatedRent;
      remaining = newAmt - calculatedRent;
    }
  
    // If NO invoices → allocate remaining to deposit
    if (invoices.length === 0 && remaining > 0) {
      depositAllocated[0].amount = remaining;
      remaining = 0;
    }
  
    setRentAmount(rentAllocated);
    setInvoiceAmounts(invoiceAllocated);
    setDepositData(depositAllocated);
  
    // 🔁 If invoices exist, apply remaining amount to invoices
    if (invoices.length > 0 && remaining > 0) {
      const sortedInvoices = [...invoices].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
      let updatedInvoicePayments = [];
  
      for (const invoice of sortedInvoices) {
        const due = parseFloat(invoice.amountDue || 0);
        if (remaining >= due) {
          updatedInvoicePayments.push({ InvoiceId: invoice.id, Amount: due });
          remaining -= due;
        } else if (remaining > 0) {
          updatedInvoicePayments.push({ InvoiceId: invoice.id, Amount: remaining });
          remaining = 0;
          break;
        }
      }
  
      setInvoiceAmounts(updatedInvoicePayments);
  
      // If anything remains after invoices, assign to deposit
      if (remaining > 0) {
        depositAllocated[0].amount = remaining;
        setDepositData(depositAllocated);
      }
    }
  };
  
  
  
  

  const handleInputChange = (index, field, value, type, invId) => {
    const numericValue =
      field === "description" ? value : parseFloat(value) || 0;

    if (type === "invoice") {
      const updatedInvoices = [...invoiceAmounts];
      const existingIndex = updatedInvoices.findIndex(
        (item) => item.InvoiceId === invId
      );

      if (existingIndex !== -1) {
        updatedInvoices[existingIndex].Amount = numericValue;
      } else {
        updatedInvoices.push({ InvoiceId: invId, Amount: numericValue });
      }

      const totalDeposit = depositData.reduce(
        (sum, dep) => sum + parseFloat(dep.amount || 0),
        0
      );
      const totalInvoice = updatedInvoices.reduce(
        (sum, inv) => sum + parseFloat(inv.Amount || 0),
        0
      );
      const remainingForRent =
        parseFloat(amount || 0) - totalInvoice - totalDeposit;

      setInvoiceAmounts(updatedInvoices);
      setRentAmount(remainingForRent > 0 ? remainingForRent : 0);
    } else if (type === "rent") {
      setRentAmount(numericValue);
      setDepositData([
        {
          description: `Deposit for ${receiptDetails.propertyAddress}`,
          amount: "",
        },
      ]);
      setInvoiceAmounts([]);
    } else {
      const updatedDeposits = [...depositData];
      updatedDeposits[index][field] = numericValue;
      setDepositData(updatedDeposits);

      const totalDeposit = updatedDeposits.reduce(
        (sum, dep) => sum + parseFloat(dep.amount || 0),
        0
      );
      const totalInvoice = invoiceAmounts.reduce(
        (sum, inv) => sum + parseFloat(inv.Amount || 0),
        0
      );
      const remainingForRent =
        parseFloat(amount || 0) - totalInvoice - totalDeposit;
      setRentAmount(remainingForRent > 0 ? remainingForRent : 0);
    }
  };

  const handleSubmit = () => {
    const totalAllocated = getTotalAllocatedAmount();

    if (totalAllocated !== amount) {
      ToastRef.current?.notify(["error", "Invalid Amount"]);
      return;
    }

    // Validate invoice overpayments
    const overpaidInvoice = invoiceAmounts.find((inv) => {
      const matchingInvoice = receiptDetails.invoiceDueList?.find(
        (i) => i.id === inv.InvoiceId
      );
      return matchingInvoice && inv.Amount > matchingInvoice.amountDue;
    });

    if (overpaidInvoice) {
      const matchingInvoice = receiptDetails.invoiceDueList.find(
        (i) => i.id === overpaidInvoice.InvoiceId
      );
      const overpaidAmount = overpaidInvoice.Amount - matchingInvoice.amountDue;

      ToastRef.current?.notify([
        "error",
        `Unable to transfer deposit to invoice "${matchingInvoice.description}" – overpaid by $${overpaidAmount}`,
      ]);
      return;
    }

    const formData = {
      Date: date,
      Amount: amount,
      RentAmount: rentAmount,
      invoicePayments: invoiceAmounts,
      Deposit: depositData[0].amount,
      DepositDescription: depositData[0].description,
    };
    onSubmit(formData);
  };

  const getTotalAllocatedAmount = () => {
    const invoiceTotal = invoiceAmounts.reduce(
      (sum, inv) => sum + (parseFloat(inv.Amount) || 0),
      0
    );
    const depositTotal = depositData.reduce(
      (sum, dep) => sum + (parseFloat(dep.amount) || 0),
      0
    );
    return (parseFloat(rentAmount) || 0) + invoiceTotal + depositTotal;
  };

  return (
    <CustomModal
      title="New Tenant Receipt"
      width={1000}
      closable={onClose}
      visible={visible}
      onCancel={onClose}
      modalData={
        <div className="details-form">
          <div className="row" style={{width:'100%'}} >
            <div className="contacts-group row">
               {Tenant ?
                <div className="pd-undefined col-lg-6 col-sm-12 ">
                  <label className="s1 mr-4">Tenant</label>
                  <div>
                  <Select
                    style={{ width: "100%", height: "40px" }}
                    className="landingPageSearchs"
                    showSearch
                    labelInValue
                    placeholder="Search for tenant"
                    filterOption={false}
                    onSearch={debounceFetcherSupplier}
                    notFoundContent={
                      fetchingSupplier ? <Spin size="small" /> : null
                    }
                    options={optionSupplier}
                    onChange={handleSearchSupplier}
                    allowClear
                    onClear={handleClearSupplier}
                    value={supplierValue}
                    disabled={!supplierValue ? false : true}
                  />
                  </div>
                </div> : null }
              
              <TextBox label="Owner" value={receiptDetails?.ownerDetails} readOnly={true} />
              <TextBox label="Property" value={receiptDetails?.propertyAddress} readOnly={true} />
              <TextBox label="Details" value={receiptDetails?.description} readOnly={true} />
              <TextBox label="Property Rent" value={receiptDetails?.rent ? "$ " + receiptDetails?.rent : null} readOnly={true} />
              <TextBox label="Date" type="date"  value={date} onChange={(e) => setDate(e.target.value)} />
              <TextBox label="Amount"  value={amount} onChange={handleAmt} />

            </div>


            <div className="row mt-3">
              <div className="col-12">
                <label className="s1 mb-2">Paid By:</label>
                <div>
                <Radio.Group
                  onChange={(e) => setSelectedPaymentMode(e.target.value)}
                  value={selectedPaymentMode}
                >
                  {ePaymentModes.map((mode) => (
                    <Radio key={mode.value} value={mode.value}>
                      {mode.label}
                    </Radio>
                  ))}
                </Radio.Group>
                </div>
              </div>
            </div>

          </div>

          {/* Rent and Invoice Table */}
          <table className="table mt-3">
            <thead >
              <tr>
                <th style={{backgroundColor:'#e2e2e2'}}>Allocation</th>
                <th style={{backgroundColor:'#e2e2e2'}}>Due</th>
                <th style={{backgroundColor:'#e2e2e2'}}>Description</th>
                <th style={{backgroundColor:'#e2e2e2'}}>Amount Due</th>
                <th style={{backgroundColor:'#e2e2e2'}}>Due Days</th>
                <th style={{backgroundColor:'#e2e2e2'}}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Rent</td>
                <td>{moment(receiptDetails.rentDue).format("DD MMM YYYY")}</td>
                <td>{receiptDetails.description}</td>
                <td>-</td>
                <td
                  style={{
                    color:
                      receiptDetails.amountDueDays < 0
                        ? "green"
                        : receiptDetails.amountDueDays > 0
                        ? "orange"
                        : "gray",
                  }}
                >
                  {receiptDetails.amountDueDays} days
                </td>
                <td>
                  <input
                    value={rentAmount}
                    className="input-box"
                    placeholder="Add rent amount"
                    onChange={(e) =>
                      handleInputChange(0, "amount", e.target.value, "rent")
                    }
                  />
                </td>
              </tr>
              {receiptDetails?.invoiceDueList?.map((row, index) => (
                <tr key={index}>
                  <td>Invoice</td>
                  <td>{moment(row.dueDate).format("DD MMM YYYY")}</td>
                  <td>{row.description}</td>
                  <td>${row.amountDue}</td>
                  <td
                    style={{
                      color:
                        row.amountDueDates < 0
                          ? "green"
                          : row.amountDueDates > 0
                          ? "orange"
                          : "gray",
                    }}
                  >
                    {row.amountDueDates} days
                  </td>
                  <td>
                    <input
                      value={
                        invoiceAmounts.find((item) => item.InvoiceId === row.id)
                          ?.Amount || ""
                      }
                      className="input-box"
                      placeholder="Add invoice amount"
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "invamount",
                          e.target.value,
                          "invoice",
                          row.id
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Deposit Table */}
          <table className="table mt-3">
            <tbody>
              {depositData.map((row, index) => (
                <tr key={index}>
                  <td>Deposit</td>
                  <td>
                    <input
                      type="text"
                      value={row.description}
                      className="description-input input-box"
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "description",
                          e.target.value,
                          "deposit"
                        )
                      }
                    />
                  </td>
                  <td style={{ display: "flex", justifyContent: "end" }}>
                    <input
                      style={{ width: "36%" }}
                      value={row.amount}
                      className="amount-input input-box"
                      placeholder="Add deposit amount"
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "amount",
                          e.target.value,
                          "deposit"
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
      footer={[
        <div className="modal-footer">
          <button className="pdb-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className={
              getTotalAllocatedAmount() !== amount
                ? "cal-exceed"
                : "pdb-save-changes"
            }
            onClick={handleSubmit}
          >
            {amount ? `$ ${amount}` : "Submit"}
          </button>
        </div>,
      ]}
    />
  );
};

export default NewTenantReceipt;
